import { DocumentNode, NetworkStatus, gql, useQuery } from '@apollo/client';
import { Heading, Text } from '@chakra-ui/react';
import dayjs from 'dayjs';
import { useRouter } from 'next/router';
import * as React from 'react';

import {
  PostingExperience,
  usePostingExperience,
} from '@/components/PostingExperience';
import { logError } from '@/imports/logging/ClientLogger';
import { Card } from '@/imports/ui/chakra/components/Card';
import { Emoji } from '@/imports/ui/chakra/components/Emoji';

import { OnboardingChecklist_Query } from './graphql-types/OnboardingChecklist_Query';

import { OnboardingChecklistItem } from './components/OnboardingChecklistItem';

const USER_FRAGMENT = gql`
  fragment OnboardingChecklist_UserFragment on User {
    _id
    canAccessCommunity
    userOnboardingChecklistState {
      userIsActive
      userHasPosted
      userHasJoinedAGroup
      userHasJoinedOrCanJoinNonGlobalSingletonGroup
      userActivationDate
      userHasFollowedTags
      userHasFollowedATagOrTagsExist
      userHasProfilePicture
    }
  }
`;

const QUERY = gql`
  query OnboardingChecklist_Query {
    currentUser {
      _id
      ...OnboardingChecklist_UserFragment
    }
    tenant {
      result {
        _id
        acronym
      }
    }
  }
  ${USER_FRAGMENT}
`;

export const OnboardingChecklist: React.FC & {
  onboardingChecklistFragment: DocumentNode;
} = () => {
  const { data, networkStatus, error } = useQuery<OnboardingChecklist_Query>(
    QUERY,
    {
      onError: logError,
    }
  );
  const [userHasSeenChecklist, setUserHasSeenChecklist] = React.useState(false);

  const postingExperience = usePostingExperience();

  const router = useRouter();

  // use NetworkStatus.loading here so we only see a spinner on initial load, and not on updates
  if (networkStatus === NetworkStatus.loading) {
    return null;
  }

  if (error) {
    return null;
  }

  if (!data?.currentUser || !data.tenant.result) {
    return null;
  }

  if (!data.currentUser.canAccessCommunity) {
    return null;
  }

  if (
    dayjs().isAfter(
      dayjs(
        data.currentUser.userOnboardingChecklistState.userActivationDate
      ).add(3, 'weeks')
    )
  ) {
    return null;
  }

  /* Use this method to keep the checkist on the page, even if it's
     completed, until a full page refresh. So if the component loads and
     all 5 items are checked, hide it, otherwise keep it visible */

  const areAllStepsCompleted =
    data.currentUser.userOnboardingChecklistState.userIsActive &&
    data.currentUser.userOnboardingChecklistState.userHasPosted &&
    (data.currentUser.userOnboardingChecklistState.userHasJoinedAGroup ||
      (!data.currentUser.userOnboardingChecklistState
        .userHasJoinedOrCanJoinNonGlobalSingletonGroup &&
        !data.currentUser.userOnboardingChecklistState.userHasJoinedAGroup)) &&
    (data.currentUser.userOnboardingChecklistState.userHasFollowedTags ||
      (!data.currentUser.userOnboardingChecklistState
        .userHasFollowedATagOrTagsExist &&
        !data.currentUser.userOnboardingChecklistState.userHasFollowedTags)) &&
    data.currentUser.userOnboardingChecklistState.userHasProfilePicture;

  if (areAllStepsCompleted && !userHasSeenChecklist) {
    return null;
  }
  !userHasSeenChecklist && setUserHasSeenChecklist(true);

  return (
    <Card p="4" pt="6" width="full">
      <Heading size="md" mb="1" px="2">
        {data.tenant.result.acronym
          ? `Welcome to the ${data.tenant.result.acronym} Online Community!`
          : 'Welcome to the Community!'}{' '}
        <Emoji symbol="👋" />
      </Heading>

      <Text color="gray.500" px="2" mb="4">
        Here are some simple steps to get you started
      </Text>

      <OnboardingChecklistItem
        onClick={() => {
          router.push('/');
        }}
        isChecked={data.currentUser.userOnboardingChecklistState.userIsActive}
      >
        Activate your account
      </OnboardingChecklistItem>

      {!!data.currentUser.userOnboardingChecklistState
        .userHasJoinedOrCanJoinNonGlobalSingletonGroup && (
        <OnboardingChecklistItem
          onClick={() => {
            router.push('/community/groups');
          }}
          isChecked={
            data.currentUser.userOnboardingChecklistState.userHasJoinedAGroup
          }
        >
          Join a group
        </OnboardingChecklistItem>
      )}

      {!!data.currentUser.userOnboardingChecklistState
        .userHasFollowedATagOrTagsExist && (
        <OnboardingChecklistItem
          onClick={() => {
            router.push('/community/tags');
          }}
          isChecked={
            data.currentUser.userOnboardingChecklistState.userHasFollowedTags
          }
        >
          Follow tags
        </OnboardingChecklistItem>
      )}

      <OnboardingChecklistItem
        onClick={() => {
          router.push(`/me/profile?uploadAvatarModalOpen=true`);
        }}
        isChecked={
          data.currentUser.userOnboardingChecklistState.userHasProfilePicture
        }
      >
        Add a profile photo
      </OnboardingChecklistItem>

      <OnboardingChecklistItem
        isChecked={data.currentUser.userOnboardingChecklistState.userHasPosted}
        onClick={() => {
          postingExperience.createPost();
        }}
      >
        Introduce yourself with a post
      </OnboardingChecklistItem>
      <PostingExperience {...postingExperience} />
    </Card>
  );
};

OnboardingChecklist.onboardingChecklistFragment = USER_FRAGMENT;
